import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SubscriptionPlansModal from './SubscriptionPlansModal';
import OnboardingCompleteModal from './OnboardingCompleteModal';
import { getCompanySubscription } from '../api/subscription';
import { startFreeTrial } from '../api';
import { toast } from 'react-toastify';

const SubscriptionGuard = ({ children }) => {
  const navigate = useNavigate();
  const authState = useSelector((store) => store.auth);
  const rawSubAdminDetail = useSelector((store) => store.subAdmin.detail);
  const mainAdminId = useSelector((store) => store.main.id);
  const authAdminId = authState?.adminId;
  const id = mainAdminId || authAdminId;
  const data = rawSubAdminDetail?.id == id ? rawSubAdminDetail : null;
  const resolvedIsCompany = authState?.user_type === "company" ? 1 : 0;
  
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showOnboardingCompleteModal, setShowOnboardingCompleteModal] = useState(false);
  const [companySubscription, setCompanySubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [subscriptionChecked, setSubscriptionChecked] = useState(false);

  // Check if company has active subscription
  const hasActiveSubscription = () => {
    // If in trial period, consider subscription active
    if (isInTrialPeriod()) return true;
    
    if (!companySubscription) return false;
    
    // Handle different possible status values
    const status = companySubscription.status?.toString().toLowerCase();
    console.log('Checking subscription status:', status); // Debug log
    
    // Consider subscription active if status is one of these values
    const activeStatuses = ['active', '1', 1, 'paid', 'subscribed', 'enabled', 'trial', 'trialing'];
    return activeStatuses.includes(status);
  };

  // Check if company is within trial period
  const isInTrialPeriod = () => {
    if (!data?.trial_end) return false;
    const trialEndDate = new Date(data.trial_end);
    const currentDate = new Date();
    return currentDate <= trialEndDate;
  };

  // Calculate remaining trial days
  const calculateTrialDaysLeft = () => {
    if (!data?.trial_end) return null;
    const now = new Date();
    const trialEnd = new Date(data.trial_end);
    const daysLeft = Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24));
    return daysLeft >= 0 ? daysLeft : 0;
  };

  // Fetch company subscription data
  const fetchCompanySubscription = async () => {
    setLoadingSubscription(true);
    try {
      const companyId = data?.company_id || authState?.company_id;
      if (companyId) {
        const response = await getCompanySubscription(companyId);
        console.log('Subscription API Response:', response); // Debug log
        
        if (response?.code === 1 && response?.data) {
          console.log('Subscription data:', response.data); // Debug log
          setCompanySubscription(response.data);
        } else {
          console.log('No subscription data found:', response); // Debug log
          setCompanySubscription(null);
        }
      }
    } catch (error) {
      console.error('Error fetching company subscription:', error);
      setCompanySubscription(null);
    } finally {
      setLoadingSubscription(false);
      setSubscriptionChecked(true);
    }
  };

  useEffect(() => {
    if (resolvedIsCompany) {
      fetchCompanySubscription();
    } else {
      setLoadingSubscription(false);
      setSubscriptionChecked(false);
    }
  }, [resolvedIsCompany]);

  const handleStartTrial = async () => {
    const companyId = data?.company_id || authState?.company_id;
    if (companyId) {
      await startFreeTrial(companyId);
      // Refresh subscription data after starting trial
      await fetchCompanySubscription();
      // Refresh page to update trial status
      window.location.reload();
    }
  };

  const handlePurchaseSubscription = () => {
    setShowOnboardingCompleteModal(false);
    setShowSubscriptionModal(true);
  };

  useEffect(() => {
    // Auto-show onboarding complete modal for companies without trial and no subscription
    // Only show warning after subscription check is complete
    // Only show if profile is complete (is_profile_complete == 1)
    if (resolvedIsCompany && subscriptionChecked && 
        data?.is_profile_complete == 1 && !isInTrialPeriod() && !hasActiveSubscription()) {
      setShowOnboardingCompleteModal(true);
    }
  }, [resolvedIsCompany, subscriptionChecked, companySubscription, data?.trial_end, data?.is_profile_complete]);

  // If not a company user, allow access
  if (!resolvedIsCompany) {
    return children;
  }

  // If loading, show loading state
  if (loadingSubscription) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If company has active subscription or is in trial period, allow access
  if (hasActiveSubscription() || isInTrialPeriod()) {
    return children;
  }

  // If company doesn't have active subscription, allow access but show onboarding complete modal as overlay
  return (
    <>
      {children}
      <OnboardingCompleteModal
        isOpen={showOnboardingCompleteModal}
        onClose={() => setShowOnboardingCompleteModal(false)}
        onStartTrial={handleStartTrial}
        onPurchaseSubscription={handlePurchaseSubscription}
        isInTrial={isInTrialPeriod()}
        trialDaysLeft={calculateTrialDaysLeft()}
        isTrialExpired={data?.trial_end && new Date() > new Date(data.trial_end)}
        companyId={id}
        hasBespokeEnquiry={data?.has_bespoke_enquiry == 1}
      />
      <SubscriptionPlansModal
        show={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        companySubscription={companySubscription}
      />
    </>
  );
};

export default SubscriptionGuard;
